// The one filter pattern in the system — the owner's ruling of 2026-08-23,
// recorded on the wireframe's own component
// (wireframes/react/src/components/BaraFiltre.tsx:1), reaffirmed as decision D2
// on 2026-09-05: "un singur tipar în tot sistemul … această versiune e super,
// peste tot".
//
// Row one never changes shape: the search box on the left, an optional
// informative slot, and a "Filtre (n)" button anchored right that opens a
// floating vertical panel holding every facet. Row two appears only when
// something is active: one removable chip per criterion and a reset at the end.
// Because the button never moves between the zero-filter and the ten-filter
// state, the control the administrator reaches for is always in the same place.
//
// AGE ships no filter bar and no popover, so this is a SIMSM composite over AGE
// primitives — placement comes from the shared anchored-overlay helper, the same
// one RowActionsMenu uses, so a panel opened near the fold flips and clamps
// instead of running off screen. It declares no design token; R2 is not engaged.
// AGE-GAP(filter-bar) · vendor/age-design/SOURCE_COMMIT 7322a157
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { MudButton, MudChip, MudIcon, MudSearchInput } from '@egov-moldova/mud-react';
import { useAnchoredOverlay } from './anchored-overlay';

// The wireframe's PanouFiltre default (components/PanouFiltre.tsx:9). A register
// whose facets need two columns — a date range reads as one control, not two —
// passes its own width rather than styling the panel from the page.
const DEFAULT_PANEL_WIDTH = 360;

/** One criterion currently narrowing the list, and how to drop it. */
export interface ActiveFilter {
  /** Stable key — the URL parameter the criterion lives in. */
  key: string;
  /** What the criterion is: "Tip Registrator". */
  label: string;
  /** What it is set to, already translated: "Farmacie". */
  value: string;
  onRemove: () => void;
}

export interface FilterBarSearch {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  ariaLabel: string;
  clearLabel: string;
}

export interface FilterBarProps {
  search?: FilterBarSearch | undefined;
  /** The facets, one under another. Omit to render a search-only bar. */
  panel?: ReactNode;
  active: ActiveFilter[];
  onReset: () => void;
  /** Anything informative that belongs on row one, between search and button. */
  info?: ReactNode;
  /** Panel width in pixels. Defaults to the wireframe's 360. */
  panelWidth?: number | undefined;
  labels: {
    /** The button: "Filtre". The count is appended by this component. */
    filters: string;
    reset: string;
    panelAriaLabel: string;
    activeAriaLabel: string;
  };
}

export function FilterBar({
  search,
  panel,
  active,
  onReset,
  info,
  panelWidth,
  labels,
}: FilterBarProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const { style } = useAnchoredOverlay({
    open,
    triggerRef,
    overlayRef: panelRef,
    align: 'end',
  });

  useEffect(() => {
    if (!open) return undefined;
    function handleClick(event: MouseEvent): void {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    }
    function handleKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <div className="simsm-filter-bar">
      <div className="simsm-filter-bar__row">
        {search ? (
          <div className="simsm-filter-bar__search">
            {/* AGE's search input, not a plain one: the wireframe's search box
                carries a leading magnifier and a trailing clear ×, and this
                component owns both, with `clearLabel` the only translated
                string it needs. */}
            <MudSearchInput
              shape="rectangular"
              size="md"
              value={search.value}
              placeholder={search.placeholder}
              aria-label={search.ariaLabel}
              clearLabel={search.clearLabel}
              // `mudInput`, never `mudChange`: the wireframe filters on every
              // keystroke, and a search that commits only on blur reads as a
              // dead control (audit, /system-admin/dashboards-admin).
              onMudInput={(e: CustomEvent<{ value: string }>) => search.onChange(e.detail.value)}
              onMudClear={() => search.onChange('')}
            />
          </div>
        ) : null}
        <span className="simsm-filter-bar__spacer" />
        {info}
        {panel ? (
          <div className="simsm-filter-bar__trigger" ref={triggerRef}>
            <MudButton
              shape="circular"
              appearance="outlined"
              variant="strict"
              size="md"
              aria-haspopup="dialog"
              aria-expanded={open}
              onClick={() => setOpen((previous) => !previous)}
            >
              <MudIcon slot="icon-start" name="filter" size={20} />
              {active.length > 0 ? `${labels.filters} (${active.length})` : labels.filters}
              <MudIcon slot="icon-end" name="chevron-bottom" size={16} />
            </MudButton>
          </div>
        ) : null}
      </div>

      {panel && open
        ? createPortal(
            <div
              className="simsm-filter-bar__panel"
              // AGE has no popover. The wireframe's PanouFiltre floats under
              // its Filtre button; mud-modal is a native <dialog> the UA
              // centres in the top layer, so it cannot be anchored to the
              // button the way useAnchoredOverlay anchors this panel.
              // eslint-disable-next-line no-restricted-syntax -- popover anchored to its button; mud-modal is a centred native dialog and AGE has no popover
              role="dialog"
              aria-label={labels.panelAriaLabel}
              ref={panelRef}
              style={{ ...style, width: panelWidth ?? DEFAULT_PANEL_WIDTH }}
            >
              {panel}
            </div>,
            document.body,
          )
        : null}

      {active.length > 0 ? (
        <div className="simsm-filter-bar__chips" aria-label={labels.activeAriaLabel}>
          {active.map((filter) => (
            // The words go in the default slot: mud-chip's `label` prop is an
            // accessible-name fallback that renders no visible text.
            // MUD puts the verb on the × ('Elimină' + the chip's name, in the page
            // language), so the chip is named by what it filters, once.
            <MudChip
              key={filter.key}
              type="input"
              size="sm"
              removable
              aria-label={`${filter.label} ${filter.value}`}
              onMudRemove={filter.onRemove}
            >
              <span className="simsm-filter-bar__chip-key">{filter.label}:</span>
              {filter.value}
            </MudChip>
          ))}
          {/* The wireframe's ResetFiltre is a bare text button, not a filled
              grey one (components/ResetFiltre.tsx:1). */}
          <MudButton
            shape="circular"
            appearance="text"
            variant="strict"
            size="sm"
            onClick={onReset}
          >
            {labels.reset}
          </MudButton>
        </div>
      ) : null}
    </div>
  );
}
